import React from 'react';
import { ScrollView, VStack, HStack, Box, Text, Heading, Divider } from '@gluestack-ui/themed';
import { usePreviewContent } from '../hooks/useModulePreviewStore';
import { useTranslation } from 'react-i18next';

/**
 * Component to display differences between module versions
 * Shows a tabular view of changes in tasks, objectives, and resources
 */
const ModuleDiffScreen = () => {
  const { t } = useTranslation();
  const { module, tasks, sourceModule, sourceTasks } = usePreviewContent();

  // Check if we have source data to compare
  if (!sourceModule || !sourceTasks) {
    return (
      <Box flex={1} p="$8" alignItems="center" justifyContent="center">
        <Text color="$textLight400">{t('admin.modulePreview.diffNotAvailable')}</Text>
      </Box>
    );
  }

  // Compare module metadata
  const moduleChanges = compareModules(sourceModule, module);

  // Compare tasks
  const taskChanges = compareTasks(sourceTasks, tasks);

  return (
    <ScrollView flex={1} p="$6">
      <VStack space="lg" width="100%">
        <Heading size="lg">{t('admin.modulePreview.versionComparison')}</Heading>
        <Text>{t('admin.modulePreview.comparingVersions', { source: sourceModule.version, target: module.version })}</Text>

        {/* Module metadata changes */}
        {moduleChanges.length > 0 && (
          <Box>
            <Heading size="md" my="$4">{t('admin.modulePreview.moduleChanges')}</Heading>
            <VStack space="sm" borderWidth={1} borderColor="$borderLight200" borderRadius="$md">
              <HStack bg="$backgroundLight100" p="$4" borderBottomWidth={1} borderBottomColor="$borderLight200">
                <Text flex={1} fontWeight="bold">{t('admin.modulePreview.field')}</Text>
                <Text flex={2} fontWeight="bold">{t('admin.modulePreview.before')}</Text>
                <Text flex={2} fontWeight="bold">{t('admin.modulePreview.after')}</Text>
              </HStack>
              {moduleChanges.map((change, index) => (
                <HStack key={index} bg={index % 2 === 0 ? "$backgroundLight50" : "$backgroundLight100"} p="$4">
                  <Text flex={1} fontWeight="bold">{change.field}</Text>
                  <Text flex={2}>{change.oldValue}</Text>
                  <Text flex={2}>{change.newValue}</Text>
                </HStack>
              ))}
            </VStack>
          </Box>
        )}

        {/* Task changes */}
        {taskChanges.length > 0 && (
          <Box>
            <Heading size="md" my="$4">{t('admin.modulePreview.taskChanges')}</Heading>
            {taskChanges.map((taskChange, taskIndex) => (
              <VStack key={taskIndex} space="sm" mb="$6">
                <Heading size="sm">
                  {taskChange.isNew ? t('admin.modulePreview.newTask') :
                   taskChange.isRemoved ? t('admin.modulePreview.removedTask') :
                   t('admin.modulePreview.taskChanges')}
                  {!taskChange.isNew && !taskChange.isRemoved && `: ${taskChange.name}`}
                </Heading>

                {taskChange.isNew && (
                  <Text color="$success700">{t('admin.modulePreview.taskAdded', { name: taskChange.name })}</Text>
                )}

                {taskChange.isRemoved && (
                  <Text color="$error700">{t('admin.modulePreview.taskRemoved', { name: taskChange.name })}</Text>
                )}

                {!taskChange.isNew && !taskChange.isRemoved && taskChange.changes.length > 0 && (
                  <VStack space="sm" borderWidth={1} borderColor="$borderLight200" borderRadius="$md">
                    <HStack bg="$backgroundLight100" p="$4" borderBottomWidth={1} borderBottomColor="$borderLight200">
                      <Text flex={1} fontWeight="bold">{t('admin.modulePreview.field')}</Text>
                      <Text flex={2} fontWeight="bold">{t('admin.modulePreview.before')}</Text>
                      <Text flex={2} fontWeight="bold">{t('admin.modulePreview.after')}</Text>
                    </HStack>
                    {taskChange.changes.map((change, index) => (
                      <HStack key={index} bg={index % 2 === 0 ? "$backgroundLight50" : "$backgroundLight100"} p="$4">
                        <Text flex={1} fontWeight="bold">{change.field}</Text>
                        <Text flex={2}>{change.oldValue}</Text>
                        <Text flex={2}>{change.newValue}</Text>
                      </HStack>
                    ))}
                  </VStack>
                )}

                {/* Resource changes */}
                {taskChange.resourceChanges && taskChange.resourceChanges.length > 0 && (
                  <VStack space="sm" mt="$4">
                    <Heading size="xs">{t('admin.modulePreview.resourceChanges')}</Heading>
                    <VStack space="sm" borderWidth={1} borderColor="$borderLight200" borderRadius="$md">
                      <HStack bg="$backgroundLight100" p="$4" borderBottomWidth={1} borderBottomColor="$borderLight200">
                        <Text flex={1} fontWeight="bold">{t('admin.modulePreview.change')}</Text>
                        <Text flex={2} fontWeight="bold">{t('admin.modulePreview.resource')}</Text>
                        <Text flex={2} fontWeight="bold">{t('admin.modulePreview.details')}</Text>
                      </HStack>
                      {taskChange.resourceChanges.map((resChange, index) => (
                        <HStack key={index} bg={index % 2 === 0 ? "$backgroundLight50" : "$backgroundLight100"} p="$4">
                          <Text
                            flex={1}
                            fontWeight="bold"
                            color={
                              resChange.changeType === 'added' ? '$success700' :
                              resChange.changeType === 'removed' ? '$error700' :
                              '$info700'
                            }
                          >
                            {resChange.changeType === 'added' ? t('admin.modulePreview.added') :
                             resChange.changeType === 'removed' ? t('admin.modulePreview.removed') :
                             t('admin.modulePreview.modified')}
                          </Text>
                          <Text flex={2}>{resChange.title}</Text>
                          <Text flex={2}>{resChange.details}</Text>
                        </HStack>
                      ))}
                    </VStack>
                  </VStack>
                )}
              </VStack>
            ))}
          </Box>
        )}

        {moduleChanges.length === 0 && taskChanges.length === 0 && (
          <Box p="$6" alignItems="center">
            <Text>{t('admin.modulePreview.noChangesDetected')}</Text>
          </Box>
        )}
      </VStack>
    </ScrollView>
  );
};

/**
 * Compare basic module properties
 */
function compareModules(sourceModule, targetModule) {
  const changes = [];

  // Compare basic properties
  const fieldsToCompare = [
    { key: 'title', label: 'Title' },
    { key: 'description', label: 'Description' },
    { key: 'learningObjectives', label: 'Learning Objectives' },
    { key: 'metadata.learningHours', label: 'Learning Hours' },
    { key: 'metadata.difficulty', label: 'Difficulty' },
  ];

  fieldsToCompare.forEach(field => {
    let sourceValue = field.key.includes('.')
      ? getNestedValue(sourceModule, field.key)
      : sourceModule[field.key];

    let targetValue = field.key.includes('.')
      ? getNestedValue(targetModule, field.key)
      : targetModule[field.key];

    // Handle special cases
    if (field.key === 'learningObjectives') {
      sourceValue = Array.isArray(sourceValue) ? sourceValue.join('\n') : sourceValue;
      targetValue = Array.isArray(targetValue) ? targetValue.join('\n') : targetValue;
    }

    if (sourceValue !== targetValue) {
      changes.push({
        field: field.label,
        oldValue: sourceValue || '(empty)',
        newValue: targetValue || '(empty)'
      });
    }
  });

  return changes;
}

/**
 * Compare tasks between versions
 */
function compareTasks(sourceTasks, targetTasks) {
  const changes = [];

  // Find removed tasks
  sourceTasks.forEach(sourceTask => {
    const matchingTask = targetTasks.find(t => t.id === sourceTask.id);
    if (!matchingTask) {
      changes.push({
        isRemoved: true,
        name: sourceTask.title,
        taskId: sourceTask.id
      });
    }
  });

  // Find new tasks and changes to existing tasks
  targetTasks.forEach(targetTask => {
    const sourceTask = sourceTasks.find(t => t.id === targetTask.id);

    // New task
    if (!sourceTask) {
      changes.push({
        isNew: true,
        name: targetTask.title,
        taskId: targetTask.id
      });
      return;
    }

    // Compare existing task
    const taskChanges = [];

    // Compare basic properties
    const fieldsToCompare = [
      { key: 'title', label: 'Title' },
      { key: 'description', label: 'Description' },
      { key: 'objectives', label: 'Objectives' },
      { key: 'instructions', label: 'Instructions' },
      { key: 'level', label: 'Level' },
      { key: 'sortIndex', label: 'Order' },
    ];

    fieldsToCompare.forEach(field => {
      const sourceValue = sourceTask[field.key];
      const targetValue = targetTask[field.key];

      if (sourceValue !== targetValue) {
        taskChanges.push({
          field: field.label,
          oldValue: sourceValue || '(empty)',
          newValue: targetValue || '(empty)'
        });
      }
    });

    // Compare resources
    const resourceChanges = [];

    // Find removed resources
    sourceTask.resources?.forEach(sourceResource => {
      const matchingResource = targetTask.resources?.find(r => r.id === sourceResource.id);
      if (!matchingResource) {
        resourceChanges.push({
          changeType: 'removed',
          title: sourceResource.title,
          details: getResourceTypeLabel(sourceResource.type)
        });
      }
    });

    // Find new and modified resources
    targetTask.resources?.forEach(targetResource => {
      const sourceResource = sourceTask.resources?.find(r => r.id === targetResource.id);

      // New resource
      if (!sourceResource) {
        resourceChanges.push({
          changeType: 'added',
          title: targetResource.title,
          details: getResourceTypeLabel(targetResource.type)
        });
        return;
      }

      // Modified resource
      if (sourceResource.title !== targetResource.title ||
          sourceResource.type !== targetResource.type ||
          sourceResource.url !== targetResource.url) {
        resourceChanges.push({
          changeType: 'modified',
          title: targetResource.title,
          details: `${getResourceTypeLabel(sourceResource.type)} → ${getResourceTypeLabel(targetResource.type)}`
        });
      }
    });

    if (taskChanges.length > 0 || resourceChanges.length > 0) {
      changes.push({
        name: targetTask.title,
        taskId: targetTask.id,
        changes: taskChanges,
        resourceChanges: resourceChanges
      });
    }
  });

  return changes;
}

/**
 * Get human-readable resource type
 */
function getResourceTypeLabel(type) {
  switch (type) {
    case 'pdf': return 'PDF';
    case 'video': return 'Video';
    case 'link': return 'Link';
    case 'image': return 'Image';
    default: return type;
  }
}

/**
 * Get a nested value from an object using a dot-notation path
 */
function getNestedValue(obj, path) {
  if (!obj) return undefined;

  const keys = path.split('.');
  let value = obj;

  for (const key of keys) {
    if (value === null || value === undefined) return undefined;
    value = value[key];
  }

  return value;
}

export default ModuleDiffScreen;